import { Checkbox } from '@thanx/uikit/checkbox'
import { Select } from '@thanx/uikit/select'
import SearchInput from 'components/SearchInput'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { StyleObject, useStyletron } from 'styletron-react'

const t = buildTranslate('components.table_toolbar')

const LOCATION_SELECT_WIDTH_PX = 150
const GROUP_GAP_PX = 16

// Design sizes the search box as a share of the table, not a fixed width — its
// right edge should land between the Customer and Location columns at any
// viewport. A fixed px width tracked neither: it looked right at ~1000px and
// stranded on a wide screen. The floor keeps the box usable once the toolbar
// wraps on narrow viewports. Exported because css() is a no-op in tests.
export const searchFieldStyle: StyleObject = {
  width: '36%',
  minWidth: '240px',
  maxWidth: '100%',
}

export type ToolbarLocationOption = { id: number; label: string }

type LocationSelectOption = { label: string; value: number | null }

type Props = {
  searchValue: string
  onSearchChange: (value: string) => void
  searchAriaLabel: string
  hideResolved: boolean
  onHideResolvedChange: (hideResolved: boolean) => void
  hideResolvedLabel: string
  searchPlaceholder?: string
  // Location filter is optional: a surface without one drops the whole trio
  // rather than forking the toolbar. Debounce lives in the consuming scene.
  locations?: ToolbarLocationOption[]
  selectedLocationId?: number | null
  onLocationChange?: (locationId: number | null) => void
}

const TableToolbar = ({
  searchValue,
  onSearchChange,
  searchAriaLabel,
  hideResolved,
  onHideResolvedChange,
  hideResolvedLabel,
  searchPlaceholder,
  locations,
  selectedLocationId,
  onLocationChange,
}: Props) => {
  const [css] = useStyletron()

  const showLocationFilter = !!locations && !!onLocationChange

  const locationOptions: LocationSelectOption[] = useMemo(
    () => [
      { label: t('all_locations'), value: null },
      ...(locations ?? []).map(location => ({
        label: location.label,
        value: location.id,
      })),
    ],
    [locations]
  )

  // Fall back to an empty selection (not locationOptions[0]) when the id doesn't
  // resolve — a stale/deleted id, or async locations still loading, must show the
  // placeholder, NOT masquerade as "All locations" over already-filtered data. A
  // null/undefined id still resolves to the "All locations" sentinel via .find.
  const selectedLocationOption =
    locationOptions.find(
      option => option.value === (selectedLocationId ?? null)
    ) ?? []

  return (
    <div
      className={`d-flex align-items-center justify-content-between ${css({
        gap: `${GROUP_GAP_PX}px`,
        flexWrap: 'wrap',
      })}`}
    >
      <div className={css(searchFieldStyle)}>
        <SearchInput
          value={searchValue}
          onChange={onSearchChange}
          ariaLabel={searchAriaLabel}
          placeholder={searchPlaceholder}
        />
      </div>

      <div
        className={`d-flex align-items-center ${css({
          gap: `${GROUP_GAP_PX}px`,
        })}`}
      >
        <Checkbox checked={hideResolved} onChange={onHideResolvedChange}>
          <span>{hideResolvedLabel}</span>
        </Checkbox>

        {showLocationFilter && (
          <div className={css({ width: `${LOCATION_SELECT_WIDTH_PX}px` })}>
            <Select
              aria-label={t('filter_by_location')}
              options={locationOptions}
              value={selectedLocationOption}
              maxDropdownHeight="200px"
              // option is [] when baseui clears on Backspace (backspaceRemoves
              // defaults true, ungated by clearable:false) — guard it or reading
              // option[0].value throws. Empty selection resets to All locations (null).
              onChange={option => onLocationChange?.(option[0]?.value ?? null)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TableToolbar
